import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarDays, Video, Loader2, Clock, X } from "lucide-react";

function fmt(startIso) {
  try {
    const d = new Date(startIso);
    return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return startIso; }
}

export function AppointmentsCard() {
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments");
      setMine(data.mine); setOpen(data.open);
    } catch (e) { if (process.env.NODE_ENV !== "production") console.error("Failed to load appointments:", e?.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const book = async (id) => {
    setBusy(id);
    try { await api.post(`/appointments/${id}/book`); toast.success("Appointment booked — check your email for the details"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not book slot"); }
    finally { setBusy(null); }
  };

  const cancel = async (id) => {
    setBusy(id);
    try { await api.post(`/appointments/${id}/cancel`); toast.success("Appointment cancelled"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-brand-line bg-white overflow-hidden" data-testid="appointments-card">
      <div className="px-6 py-4 border-b border-brand-line flex items-center gap-2">
        <CalendarDays className="w-5 h-5 text-brand-green" />
        <h2 className="font-serif text-xl text-brand-green">Appointments</h2>
      </div>
      <div className="p-6 space-y-6">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-brand-green" /></div>
        ) : (
          <>
            <div>
              <h3 className="text-sm font-semibold text-brand-ink mb-3">Your upcoming appointments</h3>
              {mine.length === 0 ? (
                <p className="text-sm text-brand-muted">You have no appointments booked yet.</p>
              ) : (
                <div className="space-y-2" data-testid="my-appointments">
                  {mine.map((a) => (
                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand-line bg-brand-admin px-4 py-3" data-testid={`appt-mine-${a.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-brand-green">{fmt(a.start_iso)}</p>
                        <p className="text-xs text-brand-muted flex items-center gap-2 mt-0.5">
                          <Clock className="w-3 h-3" /> {a.duration_min} min
                          {a.meeting_link && <a href={a.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-green underline"><Video className="w-3 h-3" /> Join video call</a>}
                        </p>
                        {a.notes && <p className="text-xs text-brand-ink mt-1">{a.notes}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => cancel(a.id)} disabled={busy === a.id}
                        className="text-brand-error hover:bg-red-50" data-testid={`appt-cancel-${a.id}`}>
                        {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-1" /> Cancel</>}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-brand-ink mb-3">Available slots to book</h3>
              {open.length === 0 ? (
                <p className="text-sm text-brand-muted">No open slots right now. Your adviser will add availability, or contact us to arrange a time.</p>
              ) : (
                <div className="space-y-2" data-testid="open-slots">
                  {open.map((a) => (
                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand-line px-4 py-3" data-testid={`appt-open-${a.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-brand-ink">{fmt(a.start_iso)}</p>
                        <p className="text-xs text-brand-muted flex items-center gap-2 mt-0.5">
                          <Clock className="w-3 h-3" /> {a.duration_min} min
                          {a.video_call && <span className="inline-flex items-center gap-1"><Video className="w-3 h-3" /> Video call</span>}
                        </p>
                      </div>
                      <Button size="sm" onClick={() => book(a.id)} disabled={busy === a.id}
                        className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid={`appt-book-${a.id}`}>
                        {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
